import { Close, Search } from "@mui/icons-material";
import { Drawer, TextField, Typography, useTheme } from "@mui/material";
import React from "react";
import { Col, Row } from "v2/components/base/Flex/ui";
import Scrollable from "v2/components/base/Scrollable/ui";
import { ReactComponent as DarkLogoIcon } from "assets/icons/logo-dark.svg";
import { BackButton, BaseButton } from "v2/components/base/Button/ui";

const SearchDrawer = ({
  handleClose,
  handleSearch,
  debouncedSearch,
  data,
  isLoadingData,
  isLoadedData,
  isLoading,
  isOpenned,
  isEmpty,
  hasAllFetched,
  loadMoreRef,
  children,
  placeholder,
  ...props
}: {
  children: React.ReactNode;
  handleClose: () => void;
  handleSearch: (search: string | null) => void;
  debouncedSearch: string | null;
  data: any[];
  isLoadingData: boolean;
  isLoadedData: boolean;
  isLoading: boolean;
  isOpenned: boolean;
  isEmpty: boolean;
  hasAllFetched: boolean;
  loadMoreRef: (node?: Element | null) => void;
  placeholder: string;
}) => {
  const theme = useTheme();

  return (
    <React.Fragment key={"bottom"}>
      <Drawer
        anchor={"bottom"}
        open={isOpenned}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "100%",
            backgroundColor: theme.palette.surface.black,
            color: theme.palette.text.primary,
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
          },
        }}>
        <Col
          sx={{
            background: "url('/search-bg.png') no-repeat center center",
            backgroundSize: "contain",
          }}
          fullWidth
          fullHeight>
          <Col
            type="cc"
            sx={{
              backdropFilter: "blur(3px)",
              background: `
                linear-gradient(
                  0deg,
                  rgba(0, 0, 0, 1) 0%,
                  rgba(0, 0, 0, 1) 20%,
                  rgba(0, 0, 0, 0.1) 100%
                ),
                linear-gradient(
                  90deg,
                  rgba(0, 0, 0, 1) 0%,
                  rgba(0, 0, 0, 0.1) 50%
                ),
                linear-gradient(
                  270deg,
                  rgba(0, 0, 0, 1) 0%,
                  rgba(0, 0, 0, 0.1) 50%
                )
              `,
            }}
            fullWidth
            fullHeight>
            <Col sx={{ maxWidth: "1200px" }} fullWidth fullHeight>
              <SearchBar
                placeholder={placeholder}
                handleChange={handleSearch}
                handleClose={handleClose}
              />
              <Row sx={{ padding: "8px 10px", overflow: "scroll" }} fullWidth fullHeight>
                <Scrollable
                  isLoading={isLoadingData}
                  isEmpty={isEmpty && isLoadedData}
                  hasAllFetched={hasAllFetched}
                  loadMoreRef={loadMoreRef}>
                  {children}
                </Scrollable>
              </Row>
            </Col>
          </Col>
        </Col>
      </Drawer>
    </React.Fragment>
  );
};

const SearchBar = ({
  placeholder,
  handleChange,
  handleClose,
}: {
  placeholder: string;
  handleChange: (value: string) => void;
  handleClose: () => void;
}) => {
  const theme = useTheme();
  return (
    <Row
      type="bc"
      sx={{
        gap: "6px",
        padding: "12px 25px",
        backgroundColor: "transparent",
      }}
      fullWidth>
      <Row type="cc" gap="16px" fullHeight>
        <BackButton title="Back" onClick={handleClose} />
      </Row>
      <Row type="bc" fullWidth>
        <SearchBarTextField placeholder={placeholder} handleChange={handleChange} />
      </Row>
    </Row>
  );
};

const SearchBarTextField = ({
  placeholder,
  handleChange,
}: {
  placeholder: string;
  handleChange: (value: string) => void;
}) => {
  const theme = useTheme();
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e.target.value);
  };
  return (
    <TextField
      sx={{
        backgroundColor: theme.palette.surface["container-bg"],
        border: `1px solid ${theme.palette.border.light}`,
        borderRadius: "16px",
        width: "100%",

        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },

        "& .MuiSelect-icon": {
          color: theme.palette.text.secondary,
          fontSize: "24px",
        },

        "& .MuiInputBase-input": {
          padding: "12px 16px",
          ...theme.typography["body-md"],

          "&::placeholder": {
            color: theme.palette.text.secondary,
          },
        },

        "& .MuiSvgIcon-root": {
          color: theme.palette.text.secondary,
        },
      }}
      placeholder={placeholder}
      onChange={handleOnChange}
    />
  );
};

export default SearchDrawer;
